import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Space, Table, Tag, Typography } from 'antd';
import type { TableProps } from 'antd';
import { LinkOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import {
  loadNodeEndpoints,
  probeNodeEndpoint,
  type NodeProbeState,
  type NodeStatus,
} from '../../services/nodeStatus';
import './index.css';

const EndpointList: React.FC = () => {
  const { t } = useTranslation();
  const [nodes, setNodes] = useState<NodeStatus[]>([]);
  const [loading, setLoading] = useState(false);

  const loadAndProbe = useCallback(async () => {
    setLoading(true);
    setNodes([]);
    const endpoints = await loadNodeEndpoints();
    setNodes(endpoints.map((endpoint) => ({ endpoint, state: 'pending', latencyMs: null })));
    const results = await Promise.all(endpoints.map(probeNodeEndpoint));
    setNodes(results);
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadAndProbe();
  }, [loadAndProbe]);

  const columns: TableProps<NodeStatus>['columns'] = [
    {
      title: t('endpointList.endpoint'),
      dataIndex: 'endpoint',
      key: 'endpoint',
      render: (endpoint: string) => (
        <Typography.Text copyable={{ text: endpoint }}>{endpoint}</Typography.Text>
      ),
    },
    {
      title: t('endpointList.status'),
      dataIndex: 'state',
      key: 'state',
      width: 120,
      render: (state: NodeProbeState) => {
        if (state === 'pending') return <Tag>{t('endpointList.probing')}</Tag>;
        return <Tag color={state === 'success' ? 'success' : 'error'}>{t(`endpointList.${state}`)}</Tag>;
      },
    },
    {
      title: t('endpointList.latency'),
      dataIndex: 'latencyMs',
      key: 'latencyMs',
      width: 130,
      render: (latencyMs: number | null) => latencyMs === null ? '-' : `${latencyMs} ms`,
    },
    {
      title: t('endpointList.action'),
      key: 'action',
      width: 110,
      render: (_value: unknown, record: NodeStatus) => (
        <Button
          type="link"
          icon={<LinkOutlined />}
          href={record.endpoint}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('endpointList.visit')}
        </Button>
      ),
    },
  ];

  return (
    <div className="endpoint-list-page">
      <Card
        title={<Typography.Title level={3} style={{ margin: 0 }}>{t('endpointList.title')}</Typography.Title>}
        extra={(
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => void loadAndProbe()}>
            {t('endpointList.reprobe')}
          </Button>
        )}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Table<NodeStatus>
            rowKey="endpoint"
            size="middle"
            loading={loading && nodes.length === 0}
            columns={columns}
            dataSource={nodes}
            pagination={false}
            scroll={{ x: 620 }}
          />
        </Space>
      </Card>
    </div>
  );
};

export default EndpointList;
